import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
} from '@mui/material';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import {
  checkEmployeeFirstPurchaseDiscount,
  applyDiscount,
  formatPriceWithDiscount
} from '../../utils/discountHelper';

interface Price {
  priceId: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
}

interface SimplePriceSelectorProps {
  prices: Price[];
  onPriceSelect?: (price: Price) => void;
  defaultInterval?: 'month' | 'year';
}

// Helper function to format price
const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};

export const SimplePriceSelector: React.FC<SimplePriceSelectorProps> = ({
  prices,
  onPriceSelect,
  defaultInterval = 'month',
}) => {
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>(defaultInterval);
  const { permissions } = useUserPermissions();

  // Find prices for each interval
  const monthlyPrice = prices.find(p => p.interval === 'month');
  const yearlyPrice = prices.find(p => p.interval === 'year');

  // Calculate savings for annual plan
  const calculateSavings = () => {
    if (!monthlyPrice || !yearlyPrice) return 0;
    const yearlyEquivalent = monthlyPrice.amount * 12;
    return yearlyEquivalent - yearlyPrice.amount;
  };

  const savings = calculateSavings();
  const savingsPercentage = monthlyPrice && yearlyPrice 
    ? Math.round((savings / (monthlyPrice.amount * 12)) * 100)
    : 0;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInterval = event.target.value as 'month' | 'year';
    setSelectedInterval(newInterval);

    // Notify parent of selection
    if (onPriceSelect) {
      const selectedPrice = newInterval === 'month' ? monthlyPrice : yearlyPrice;
      if (selectedPrice) {
        onPriceSelect(selectedPrice);
      }
    }
  };

  // Auto-select single price option on mount
  useEffect(() => {
    if (prices.length === 1 && onPriceSelect) {
      onPriceSelect(prices[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices.length]); // Only re-run when prices array length changes

  // If only one price option, show it without toggle
  if (prices.length === 1) {
    const singlePrice = prices[0];
    const discountInfo = checkEmployeeFirstPurchaseDiscount(permissions, singlePrice.interval);
    const discountedAmount = discountInfo.isEligible 
      ? applyDiscount(singlePrice.amount, discountInfo.discountPercentage)
      : singlePrice.amount;
    const priceDisplay = formatPriceWithDiscount(
      singlePrice.amount, 
      discountedAmount, 
      singlePrice.currency
    );
    
    return (
      <Box>
        {discountInfo.isEligible && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {discountInfo.discountMessage}
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {priceDisplay.hasDiscount ? (
            <>
              <Typography 
                variant="body2" 
                component="span" 
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                {priceDisplay.originalFormatted}
              </Typography>
              <Typography variant="h6" component="span" color="success.main">
                {priceDisplay.discountedFormatted}
              </Typography>
            </>
          ) : (
            <Typography variant="h6" component="span">
              {formatPrice(singlePrice.amount, singlePrice.currency)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            per {singlePrice.interval}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Apply discount to prices if eligible  
  const getDiscountedPrice = (price: Price | undefined) => {
    if (!price) return null;
    const discountInfo = checkEmployeeFirstPurchaseDiscount(permissions, price.interval);
    const discountedAmount = discountInfo.isEligible 
      ? applyDiscount(price.amount, discountInfo.discountPercentage)
      : price.amount;
    return formatPriceWithDiscount(price.amount, discountedAmount, price.currency);
  };

  const monthlyPriceDisplay = getDiscountedPrice(monthlyPrice);
  const yearlyPriceDisplay = getDiscountedPrice(yearlyPrice);
  
  // Check if monthly discount applies for the alert message
  const monthlyDiscountInfo = checkEmployeeFirstPurchaseDiscount(permissions, 'month');

  return (
    <Box>
      {monthlyDiscountInfo.isEligible && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {monthlyDiscountInfo.discountMessage}
        </Alert>
      )}
      <FormControl component="fieldset">
        <RadioGroup
          value={selectedInterval}
          onChange={handleChange}
          aria-label="subscription interval"
        >
          {monthlyPrice && monthlyPriceDisplay && (
            <FormControlLabel
              value="month"
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {monthlyPriceDisplay.hasDiscount ? (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                      >
                        {monthlyPriceDisplay.originalFormatted}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }} color="success.main">
                        {monthlyPriceDisplay.discountedFormatted}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {formatPrice(monthlyPrice.amount, monthlyPrice.currency)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    / month
                  </Typography>
                </Box>
              }
              sx={{ mb: 1 }}
            />
          )}
          
          {yearlyPrice && yearlyPriceDisplay && (
            <FormControlLabel
              value="year"
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {yearlyPriceDisplay.hasDiscount ? (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                      >
                        {yearlyPriceDisplay.originalFormatted}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }} color="success.main">
                        {yearlyPriceDisplay.discountedFormatted}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {formatPrice(yearlyPrice.amount, yearlyPrice.currency)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    / year
                  </Typography>
                  {savings > 0 && (
                    <Chip
                      label={`Save ${savingsPercentage}%`}
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              }
            />
          )}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};